<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { type AdUnitLocationEnum, getAdUnit, type GetAdUnitResponse, getAdUnitToDisplay, type GetAdUnitToDisplayResponse, registerAdUnitClick } from '@/api';
import type { LocationQueryValue } from 'vue-router';

const props = defineProps<{
  adUnitId?: number;
  adUnitLocation?: AdUnitLocationEnum;
  cityId?: number | LocationQueryValue | LocationQueryValue[];
}>();

if (props.adUnitId && props.adUnitLocation) throw new Error('Cannot specify both adUnitId and adUnitLocation.');

const { t } = useI18n();

const adUnit = ref<GetAdUnitResponse | GetAdUnitToDisplayResponse | null>(null);
const loading = ref(true);

const isImageAd = computed(() => {
  if (!adUnit.value) return false;
  return adUnit.value.adUnitFileType === 'JPEG' || adUnit.value.adUnitFileType === 'GIF';
});

const fetchAdUnit = async () => {
  try {
    loading.value = true;

    const response = props.adUnitId
      ? await getAdUnit({ path: { adUnitId: props.adUnitId } })
      : await getAdUnitToDisplay({
          query: {
            adUnitLocation: props.adUnitLocation!,
          },
        });

    if (response.data) {
      adUnit.value = response.data;
    } else {
      console.debug('No ad unit data received');
    }
  } catch (err) {
    console.error('Failed to fetch ad unit:', err);
  } finally {
    loading.value = false;
  }
};

const handleAdClick = async () => {
  if (!adUnit.value) return;

  try {
    const clickParams = {
      path: {
        adUnitId: adUnit.value.adUnitId,
      },
      query: {},
    };

    // Include cityId if provided
    if (props.cityId) {
      clickParams.query = { cityId: props.cityId };
    }

    await registerAdUnitClick(clickParams);

    // Open the ad URL in a new window/tab
    if (adUnit.value.url) {
      window.open(adUnit.value.url, '_blank', 'noopener,noreferrer');
    }
  } catch (err) {
    console.error('Failed to register ad click:', err);
    // Still open the URL even if tracking fails
    if (adUnit.value.url) {
      window.open(adUnit.value.url, '_blank', 'noopener,noreferrer');
    }
  }
};

onMounted(() => {
  fetchAdUnit();
});
</script>

<template>
  <div class="mx-2 my-6 max-w-full">
    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center p-4">
      <svg aria-hidden="true" class="fill-ph-green-600 inline h-6 w-6 animate-spin text-gray-200" fill="none" viewBox="0 0 100 101" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    </div>

    <!-- Ad content -->
    <div v-else-if="adUnit" class="w-full">
      <!-- Image ad -->
      <button
        v-if="isImageAd"
        :aria-label="adUnit.altText || t('components.ad-unit.advertisement')"
        class="focus:ring-ph-green-500 mx-auto block max-w-full cursor-pointer rounded transition-opacity hover:opacity-90 focus:opacity-90 focus:ring-2 focus:ring-offset-2 focus:outline-none"
        @click="handleAdClick"
      >
        <img :alt="adUnit.altText || t('components.ad-unit.advertisement')" :src="adUnit.filename" class="mx-auto h-auto max-w-full rounded" loading="lazy" />
      </button>

      <!-- Text/HTML ad -->
      <button
        v-else
        :aria-label="adUnit.altText || t('components.ad-unit.advertisement')"
        class="focus:ring-ph-green-500 mx-auto block max-w-full cursor-pointer rounded border border-gray-200 bg-gray-50 p-4 transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
        @click="handleAdClick"
      >
        <span class="block text-center">
          <span class="font-medium text-gray-700">{{ adUnit.altText || t('components.ad-unit.advertisement') }}</span>
          <span class="mt-1 text-sm text-gray-500">{{ adUnit.filename }}</span>
        </span>
      </button>
    </div>
  </div>
</template>
